import Axios from 'axios';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import DetectionView from '@/components/admin/PrintTextComparison/DetectionView';
import FileField from '@/components/admin/PrintTextComparison/FileField';
import LessonMatchBanner from '@/components/admin/PrintTextComparison/LessonMatchBanner';
import LessonPicker from '@/components/admin/PrintTextComparison/LessonPicker';
import Results from '@/components/admin/PrintTextComparison/Results';
import TabButton from '@/components/admin/PrintTextComparison/TabButton';
import IdmlDropOverlay from '@/components/admin/PrintTextComparison/IdmlDropOverlay';
import { calloutClass, toneText } from '@/components/admin/PrintTextComparison/tone';
import { AnalyzeResponse } from '@/components/admin/PrintTextComparison/types';
import UnmatchedCallout from '@/components/admin/PrintTextComparison/UnmatchedCallout';
import usePageIdmlDrop from '@/components/admin/PrintTextComparison/usePageIdmlDrop';
import PourView from '@/components/admin/PrintTextPour/PourView';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import * as Routes from '@/modules/routes';
import cn from '@/utils/cn';

interface Props {
  analyzePath: string;
  // An .idml handed over by another page (the SPA sections page's page-wide drop), analyzed on mount.
  initialFile?: File | null;
  // Fixed lesson + program (point-of-use on a lesson page) vs. omitted (standalone page → picker).
  lessonId?: number;
  // Called when a page-wide drop delivers an .idml (e.g. the sections page flips to this tab).
  onFileAccepted?: () => void;
  programId?: number;
  workspaceLayout?: 'split' | 'tabs';
}

type Tab = 'compare' | 'pour' | 'detected';

const TAB_BAR = 'tw-mt-6 tw-flex tw-items-center tw-border-0 tw-border-b tw-border-solid tw-border-spa-border';

// Combined Print tools. One upload + one Analyze runs a single pass over the print IDML that yields,
// per section, the comparison verdict/diff AND the draft pour HTML — so both tabs are populated at
// once. Used standalone (with a picker, full-width side-by-side pour) and point-of-use (fixed lesson,
// tabbed pour to fit the narrower page).
const PrintTools = ({
  analyzePath, initialFile, lessonId: fixedLessonId, onFileAccepted, programId: fixedProgramId, workspaceLayout,
}: Props) => {
  const [pickedLessonId, setPickedLessonId] = useState<number | null>(null);
  const [pickedProgramId, setPickedProgramId] = useState<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [response, setResponse] = useState<AnalyzeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('compare');
  const [showMismatched, setShowMismatched] = useState(false);

  const standalone = fixedLessonId === undefined;
  const lessonId = fixedLessonId ?? pickedLessonId;
  const programId = fixedProgramId ?? pickedProgramId;
  const pourLayout = workspaceLayout ?? (standalone ? 'split' : 'tabs');

  const selectTab = (tab: Tab) => {
    setActiveTab(tab);
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  };

  // Link to a section's online editor, once we know the lesson + program context. A synthetic
  // print-only row (null id) has no online section to edit.
  const editHref = (sectionId: number | null) => {
    if (sectionId == null || lessonId == null || programId == null) return null;
    return Routes.edit_admin_section_path(sectionId, { lesson_id: lessonId, program_id: programId });
  };

  // The student-facing preview — where the section edit page's "Save & Preview" lands.
  const previewHref = (sectionId: number | null) => {
    if (sectionId == null || programId == null) return null;
    return Routes.shared_section_path(sectionId, { program_id: programId });
  };

  const analyze = async (idml: File | null = file) => {
    if (!idml || !lessonId) return;

    const formData = new FormData();
    formData.append('idml', idml);
    formData.append('lesson_id', String(lessonId));

    try {
      const { data } = await Axios.post<AnalyzeResponse>(analyzePath, formData);
      setResponse(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Something went wrong analyzing the print file.');
    }
  };

  const runAnalysis = async (idml: File) => {
    setLoading(true);
    setError(null);
    setResponse(null);
    setShowMismatched(false);
    await analyze(idml);
    setLoading(false);
  };

  // A dropped file analyzes immediately when the lesson is known (fixed, or already picked on
  // the standalone page) — otherwise it just loads, waiting for the picker.
  const acceptDroppedFile = (dropped: File) => {
    setFile(dropped);
    setError(null);
    onFileAccepted?.();
    if (lessonId) runAnalysis(dropped);
  };
  const dragging = usePageIdmlDrop(acceptDroppedFile);

  useEffect(() => {
    if (initialFile) acceptDroppedFile(initialFile);
  }, [initialFile]);

  // After a save from the Pour tab the online text changed: sync the edited pane right away, then
  // re-run the analysis in the background (results stay on screen) so verdicts and diffs reflect
  // the new text.
  const applySectionUpdate = async (sectionId: number, html: string) => {
    setResponse(prev => (prev && {
      ...prev,
      sections: prev.sections.map(s => (s.sectionId === sectionId ? { ...s, currentHtml: html } : s)),
    }));
    setRefreshing(true);
    await analyze();
    setRefreshing(false);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (file && lessonId) runAnalysis(file);
  };

  const wrapper = standalone
    ? 'tw-font-tci tw-p-6'
    : 'tw-font-tci tw-px-4';
  const blurb = standalone
    ? 'Pick a program and lesson, upload the final print IDML, then review which sections changed '
      + 'and the draft pour generated from the print copy.'
    : "Upload this lesson's final print IDML, then review which sections changed and the draft pour "
      + 'generated from the print copy.';

  return (
    <div className={wrapper}>
      {/* Portaled to body: on the sections page this whole subtree is display:none behind the
          tab bar, which would hide even a fixed-position overlay. */}
      {dragging && createPortal(<IdmlDropOverlay />, document.body)}
      {standalone && <h1 className="tw-m-0 tw-text-xl tw-font-bold tw-text-spa-heading">Print Tools</h1>}
      <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-spa-secondary">{blurb}</p>

      <form className="tw-mt-4 tw-flex tw-flex-wrap tw-items-end tw-gap-4" onSubmit={handleSubmit}>
        {standalone && <LessonPicker onLessonChange={setPickedLessonId} onProgramChange={setPickedProgramId} />}
        <FileField file={file} onFileChange={setFile}>
          <SPAButton disabled={!file || !lessonId || loading} type="submit" variant="primary">
            {loading ? 'Analyzing…' : 'Analyze'}
          </SPAButton>
        </FileField>
      </form>

      {error && (
        <div className={cn('tw-mt-4', calloutClass('error'))}>
          <p className={cn('tw-m-0 tw-text-sm', toneText.error)}>{error}</p>
        </div>
      )}

      {response && (
        <>
          <LessonMatchBanner lessonTitle={response.lesson.title} printLesson={response.printLesson} />
          {response.printLesson?.matches === false && !showMismatched ? (
            // A wrong-file diff is all noise — hold the results back until the mismatch is resolved
            // (or deliberately overridden, e.g. a lesson that was retitled online).
            <div className="tw-mt-3">
              <SPAButton size="xs" variant="outline" onClick={() => setShowMismatched(true)}>
                Show results anyway
              </SPAButton>
            </div>
          ) : (
            <>
              {response.detected.stories.unclaimed > 0 && (
                <UnmatchedCallout
                  count={response.detected.stories.unclaimed}
                  onReview={() => selectTab('detected')}
                />
              )}
              <div className={TAB_BAR}>
                <TabButton active={activeTab === 'compare'} onClick={() => selectTab('compare')}>
                  Compare to Print
                </TabButton>
                <TabButton active={activeTab === 'pour'} onClick={() => selectTab('pour')}>
                  Pour from Print (Draft)
                </TabButton>
                <TabButton active={activeTab === 'detected'} onClick={() => selectTab('detected')}>
                  Detected in Print
                </TabButton>
              </div>

              <div className={cn(activeTab !== 'detected' && 'tw-hidden')}>
                <DetectionView detected={response.detected} />
              </div>
              <div className={cn(activeTab !== 'compare' && 'tw-hidden')}>
                <Results
                  csv={response.csv}
                  editHref={editHref}
                  lessonId={response.lesson.id}
                  previewHref={previewHref}
                  sections={response.sections}
                />
              </div>
              <div className={cn(activeTab !== 'pour' && 'tw-hidden')}>
                <PourView
                  editHref={editHref}
                  layout={pourLayout}
                  previewHref={previewHref}
                  refreshing={refreshing}
                  sections={response.sections}
                  onSectionSaved={applySectionUpdate}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PrintTools;
